import React, { useState } from "react";
import { Send, Phone, Mail, MapPin, Clock, CheckCircle, Loader2, PhoneCall, MessageSquare } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import StarRating from "./StarRating";
import CallbackForm from "./CallbackForm";
import { companyInfo } from "../data/mock";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [mode, setMode] = useState("callback");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Send to backend API
      await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        message: formData.message,
      });

      setIsSubmitted(true);
      toast.success("Uw bericht is succesvol verzonden!");
      
      // Reset form after a delay
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({ name: "", email: "", phone: "", message: "" });
      }, 5000);
      
    } catch (error) {
      console.error("Error submitting contact form:", error);
      toast.error("Er ging iets mis. Probeer het opnieuw of bel ons direct.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      label: "Telefoon",
      value: companyInfo.phone,
      href: `tel:${companyInfo.phoneClean}`,
    },
    {
      icon: Mail,
      label: "Email",
      value: companyInfo.email,
      href: `mailto:${companyInfo.email}`,
    },
    {
      icon: MapPin,
      label: "Werkgebied",
      value: companyInfo.location,
      href: null,
    },
    {
      icon: Clock,
      label: "Bereikbaar",
      value: "Ma - Vr: 08:00 - 18:00",
      href: null,
    },
  ];

  return (
    <section id="contact" className="py-24 bg-gradient-to-br from-slate-900 via-sky-900 to-teal-900 relative overflow-hidden" aria-labelledby="contact-heading">
      {/* Background decorations */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-sky-500/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1 bg-white/10 backdrop-blur-md text-white rounded-full text-sm font-semibold mb-4 border border-white/20">
            Contact
          </span>
          <h2 id="contact-heading" className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            Liever teruggebeld worden?
          </h2>
          <p className="text-lg text-white/70 max-w-2xl mx-auto">
            Laat alleen uw naam en nummer achter en Felix belt u binnen één werkdag terug.
            Liever mailen? Stuur een bericht via het formulier.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div className="space-y-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {contactInfo.map((item, index) => (
                <div
                  key={index}
                  className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/10 hover:bg-white/15 transition-colors"
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-sky-500 to-teal-500 rounded-xl flex items-center justify-center mb-4">
                    <item.icon className="w-6 h-6 text-white" />
                  </div>
                  <p className="text-white/60 text-sm mb-1">{item.label}</p>
                  {item.href ? (
                    <a
                      href={item.href}
                      className="text-white font-semibold text-lg hover:text-sky-400 transition-colors"
                    >
                      {item.value}
                    </a>
                  ) : (
                    <p className="text-white font-semibold text-lg">{item.value}</p>
                  )}
                </div>
              ))}
            </div>

            {/* Google Reviews Badge */}
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/10">
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-white rounded-xl flex items-center justify-center">
                  <svg viewBox="0 0 24 24" className="w-10 h-10">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                </div>
                <div>
                  <p className="text-white font-bold text-lg">Google Reviews</p>
                  <div className="flex items-center space-x-2">
                    <span className="text-amber-400 font-bold text-2xl">{companyInfo.rating}</span>
                    <StarRating rating={companyInfo.rating} emptyClass="text-white/30" />
                    <span className="text-white/60">({companyInfo.reviewCount} reviews)</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-3xl p-8 shadow-2xl">
            <div role="tablist" aria-label="Contactvorm" className="grid grid-cols-2 gap-1 bg-slate-100 rounded-xl p-1 mb-6">
              <button
                type="button"
                role="tab"
                aria-selected={mode === "callback"}
                onClick={() => setMode("callback")}
                data-testid="contact-tab-callback"
                className={`flex items-center justify-center gap-2 h-11 rounded-lg text-sm font-semibold transition-all ${mode === "callback" ? "bg-white text-sky-700 shadow" : "text-slate-600 hover:text-slate-800"}`}
              >
                <PhoneCall className="w-4 h-4" /> Bel mij terug
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={mode === "message"}
                onClick={() => setMode("message")}
                data-testid="contact-tab-message"
                className={`flex items-center justify-center gap-2 h-11 rounded-lg text-sm font-semibold transition-all ${mode === "message" ? "bg-white text-sky-700 shadow" : "text-slate-600 hover:text-slate-800"}`}
              >
                <MessageSquare className="w-4 h-4" /> Stuur een bericht
              </button>
            </div>
            {mode === "callback" ? (
              <CallbackForm source="contact" />
            ) : isSubmitted ? (
              <div className="flex flex-col items-center justify-center h-full py-12">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
                  <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
                <h3 className="text-2xl font-bold text-slate-800 mb-2">
                  Bedankt voor uw bericht!
                </h3>
                <p className="text-slate-600 text-center">
                  Wij nemen zo snel mogelijk contact met u op.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-slate-700 font-medium">
                    Naam *
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Uw naam"
                    className="h-12 rounded-xl border-slate-200 focus:border-sky-500 focus:ring-sky-500"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-slate-700 font-medium">
                      Email *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="uw@email.nl"
                      className="h-12 rounded-xl border-slate-200 focus:border-sky-500 focus:ring-sky-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-slate-700 font-medium">
                      Telefoon
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="06 - 12 34 56 78"
                      className="h-12 rounded-xl border-slate-200 focus:border-sky-500 focus:ring-sky-500"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="text-slate-700 font-medium">
                    Bericht *
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    placeholder="Typ hier uw bericht..."
                    rows={5}
                    className="rounded-xl border-slate-200 focus:border-sky-500 focus:ring-sky-500 resize-none"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full h-14 bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600 text-white text-lg font-semibold rounded-xl shadow-lg shadow-sky-500/25 hover:shadow-sky-500/40 transition-all"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Verzenden...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Verstuur Bericht
                    </>
                  )}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
